import { BlockServiceTheme } from "@redotech/redo-block-service-common/messages";
import { localThemeVariables } from "./custom-html-theme";

/** Marks a document the isolator wrote, so a blanked frame is recognizable. */
export const ISOLATED_BODY_ATTRIBUTE = "data-redo-isolated";

/** Mirrors the baseline the live portal applies: redo/return/app/src/styles.css. */
const PORTAL_RESET_RULE =
  "html * { background: none; border: none; color: inherit; text-decoration: inherit }";

function baselineStyle(wrapper: HTMLElement | null): string {
  const bodyDeclarations = ["margin: 0", "color: #181818", "line-height: 1.5"];
  // The canvas resolves the same theme fonts the portal does. Read them off the
  // parent, since the wrapper itself zeroes font-size and line-height.
  const canvasStyle = wrapper?.parentElement
    ? getComputedStyle(wrapper.parentElement)
    : undefined;
  if (canvasStyle?.fontFamily) {
    bodyDeclarations.push(`font-family: ${canvasStyle.fontFamily}`);
  }
  if (canvasStyle?.fontSize) {
    bodyDeclarations.push(`font-size: ${canvasStyle.fontSize}`);
  }
  return `${PORTAL_RESET_RULE} body { ${bodyDeclarations.join("; ")} }`;
}

/**
 * The full document the isolator writes: a doctype so merchant CSS renders in
 * standards mode, the portal baseline cascade, and a marked body.
 */
export function isolatedDocumentHtml(
  wrapper: HTMLElement | null,
  bodyHtml: string,
  theme?: BlockServiceTheme,
): string {
  return `<!doctype html><html><head><style>${baselineStyle(wrapper)}${themeStyle(theme)}</style></head><body ${ISOLATED_BODY_ATTRIBUTE}>${bodyHtml}</body></html>`;
}

/**
 * The page typography the shopper's page applies, so the preview shows the
 * block's real fonts rather than the builder's. Same rules as block.html.
 */
function themeStyle(theme: BlockServiceTheme | undefined): string {
  const vars = Object.entries(localThemeVariables(theme) ?? {})
    .map(([name, value]) => `${name}: ${value}`)
    .join("; ");
  if (!vars) {
    return "";
  }
  return (
    `${theme?.fontFaces ?? ""} :root { ${vars} }` +
    " body { font-family: var(--custom-html-body-font-family); color: var(--custom-html-body-font-color) }" +
    " h1, h2, h3, h4, h5, h6 { font-family: var(--custom-html-header-font-family, var(--custom-html-body-font-family)); color: var(--custom-html-header-font-color, var(--custom-html-body-font-color)) }"
  );
}
